// import React from "react";
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Typography,
  Avatar,
  Grid,
  Divider,
  Paper,
  Container,
} from "@mui/material";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import BusinessIcon from "@mui/icons-material/Business";
import VerifiedIcon from "@mui/icons-material/Verified";
import { useGetMyProfile } from "../../hooks/query/useUser";

// Custom Theme
const theme = createTheme({
  palette: {
    primary: {
      main: "#bb0000", // Custom primary color
      light: "#e63434",
      dark: "#880000",
    },
    background: {
      default: "#f4f4f4",
    },
  },
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
  },
  components: {
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          transition: "transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out",
          "&:hover": {
            transform: "translateY(-10px)",
            boxShadow: "0 10px 20px rgba(187, 0, 0, 0.2)",
          },
        },
      },
    },
  },
});

const ProfilePage = () => {
  const { data: profile, isLoading, error } = useGetMyProfile();

  if (isLoading) {
    return (
      <ThemeProvider theme={theme}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            height: "100vh",
            backgroundColor: theme.palette.background.default,
          }}
        >
          <Typography variant="h6" color="primary">
            Loading profile...
          </Typography>
        </Box>
      </ThemeProvider>
    );
  }

  if (error) {
    return (
      <ThemeProvider theme={theme}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            height: "100vh",
            backgroundColor: theme.palette.background.default,
          }}
        >
          <Typography variant="h6" color="error">
            Failed to load profile.
          </Typography>
        </Box>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <Box
        sx={{
          backgroundColor: theme.palette.background.default,
          minHeight: "100vh",
          py: 4,
        }}
      >
        <Container maxWidth="md">
          <Paper
            elevation={3}
            sx={{
              borderRadius: 4,
              overflow: "hidden",
              background: "linear-gradient(145deg, #ffffff, #f0f0f0)",
            }}
          >
            <Card
              sx={{
                boxShadow: "none",
                borderRadius: 0,
                "&:hover": { transform: "none" },
              }}
            >
              <CardHeader
                avatar={
                  <Avatar
                    sx={{
                      bgcolor: theme.palette.primary.main,
                      width: 72,
                      height: 72,
                      boxShadow: "0 4px 10px rgba(187, 0, 0, 0.3)",
                    }}
                    alt={profile?.name}
                    src={profile?.profileImageUrl || undefined}
                  >
                    {/* If profileImageUrl doesn't exist, display the initial */}
                    {!profile?.profileImageUrl && profile?.name?.[0]}
                  </Avatar>
                }
                title={
                  <Typography
                    variant="h4"
                    sx={{
                      fontWeight: 700,
                      color: theme.palette.primary.dark,
                      mb: 0.5,
                    }}
                  >
                    {profile?.name}
                  </Typography>
                }
                subheader={
                  <Typography
                    variant="subtitle1"
                    color="textSecondary"
                    sx={{ fontWeight: 500 }}
                  >
                    {profile?.role?.displayName}
                  </Typography>
                }
                sx={{ pb: 1 }}
              />
              <Divider variant="middle" />
              <CardContent sx={{ pt: 3 }}>
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6}>
                    <Box
                      sx={{
                        p: 2,
                        bgcolor: "rgba(187, 0, 0, 0.05)",
                        borderRadius: 2,
                      }}
                    >
                      <Typography
                        variant="body1"
                        color="textSecondary"
                        gutterBottom
                      >
                        Email
                      </Typography>
                      <Typography variant="h6" color="primary">
                        {profile?.email}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Box
                      sx={{
                        p: 2,
                        bgcolor: "rgba(187, 0, 0, 0.05)",
                        borderRadius: 2,
                      }}
                    >
                      <Typography
                        variant="body1"
                        color="textSecondary"
                        gutterBottom
                      >
                        Role
                      </Typography>
                      <Typography variant="h6" color="primary">
                        {profile?.role?.displayName}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Box
                      sx={{
                        p: 2,
                        bgcolor: "rgba(187, 0, 0, 0.05)",
                        borderRadius: 2,
                        display: "flex",
                        alignItems: "center",
                      }}
                    >
                      <Typography
                        variant="body1"
                        color="textSecondary"
                        gutterBottom
                      >
                        Account Status
                      </Typography>
                      <Box
                        component="span"
                        sx={{
                          ml: 1,
                          color: "green",
                          display: "flex",
                          alignItems: "center",
                        }}
                      >
                        Verified{" "}
                        <VerifiedIcon fontSize="small" sx={{ ml: 0.5 }} />
                      </Box>
                    </Box>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>

            <Divider variant="middle" />

            <Card
              sx={{
                boxShadow: "none",
                borderRadius: 0,
                "&:hover": { transform: "none" },
              }}
            >
              <CardHeader
                avatar={
                  <Avatar
                    sx={{
                      bgcolor: theme.palette.primary.light,
                      width: 72,
                      height: 72,
                      boxShadow: "0 4px 10px rgba(187, 0, 0, 0.3)",
                    }}
                  >
                    <BusinessIcon />
                  </Avatar>
                }
                title={
                  <Typography
                    variant="h4"
                    sx={{
                      fontWeight: 700,
                      color: theme.palette.primary.dark,
                    }}
                  >
                    Tenant Information
                  </Typography>
                }
                sx={{ pb: 1 }}
              />
              <CardContent>
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6}>
                    <Box
                      sx={{
                        p: 2,
                        bgcolor: "rgba(187, 0, 0, 0.05)",
                        borderRadius: 2,
                      }}
                    >
                      <Typography
                        variant="body1"
                        color="textSecondary"
                        gutterBottom
                      >
                        Tenant Name
                      </Typography>
                      <Typography variant="h6" color="primary">
                        {profile?.tenant?.name}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Box
                      sx={{
                        p: 2,
                        bgcolor: "rgba(187, 0, 0, 0.05)",
                        borderRadius: 2,
                      }}
                    >
                      <Typography
                        variant="body1"
                        color="textSecondary"
                        gutterBottom
                      >
                        Tenant Code
                      </Typography>
                      <Typography variant="h6" color="primary">
                        {profile?.tenant?.shortCode}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Box
                      sx={{
                        p: 2,
                        bgcolor: "rgba(187, 0, 0, 0.05)",
                        borderRadius: 2,
                      }}
                    >
                      <Typography
                        variant="body1"
                        color="textSecondary"
                        gutterBottom
                      >
                        Tenant Type
                      </Typography>
                      <Typography variant="h6" color="primary">
                        {profile?.tenant?.tenantType?.displayName}
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Paper>
        </Container>
      </Box>
    </ThemeProvider>
  );
};

export default ProfilePage;
